import { GetStaticProps, NextPage } from 'next';
import { groq } from 'next-sanity';
import Head from 'next/head';
import React from 'react';
import AngleButton from '../components/AngleButton';
import FirstStepsContainer from '../components/how-it-works/FirstStepsContainer';
import QuestionsContainer from '../components/how-it-works/QuestionsContainer';
import { getClient } from '../lib/sanity.server';
import ChopDescription from '../components/how-it-works/ChopDescription';
import HowCanWeHelp from '../components/how-it-works/HowCanWeHelp';
import BestPractices from '../components/how-it-works/BestPractices';
import { WistiaEmbed } from '../components/WistiaEmbed';

interface StaticProps {
  title: string;
  seo?: {
    description: string;
    imageUrl: string;
  };
}

export const getStaticProps: GetStaticProps<StaticProps> = async ({ preview = false }) => {
  const howItWorksPageQuery = groq`
    *[_id == "singletonHowItWorks"][0] {
      title,
      seo {
        description,
        "imageUrl": openGraphImage.asset->url
      }
    }
  `;

  const homePageData = await getClient(preview).fetch<StaticProps>(howItWorksPageQuery);

  return {
    props: {
      ...homePageData,
    },
  };
};

const HowItWorks: NextPage<StaticProps> = ({ title, seo }) => {
  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name='title' content={title} />
        {seo && (
          <>
            <meta name='description' content={seo.description} />
            <meta property='og:image' content={seo.imageUrl} />
          </>
        )}
      </Head>
      <div className='items-center max-w-6xl mx-auto mb-8 text-center sm:grid sm:grid-cols-12'>
        <div className='sm:col-start-2 sm:col-span-10'>
          <h1 className='mt-16 mb-10 title sm:mt-24 lg:mt-32 lg:mb-16'>
            Learn How Church Online Platform Works
          </h1>
        </div>
        <div className='sm:col-start-1 sm:col-span-12'>
          <WistiaEmbed assetId={'fiu77titwf'} />
        </div>
        <div className='sm:col-start-3 sm:col-span-8'>
          <ChopDescription />
        </div>
        <div className='sm:col-start-3 sm:col-span-8'>
          <h4 className='w-full mt-16 group-header lg:mt-24'>Five steps to your first service.</h4>
        </div>
        <div className='sm:col-start-4 lg:col-start-1 sm:col-span-6 lg:col-span-12'>
          <FirstStepsContainer />
        </div>
        <div className='sm:col-start-3 sm:col-span-8'>
          <div className='flex w-full'>
            <div className='mx-auto mt-6 lg:mt-8'>
              <AngleButton text='Explore Features' link='/features' />
            </div>
          </div>
        </div>
        <div className='sm:col-start-3 sm:col-span-8'>
          <HowCanWeHelp />
        </div>
        <div className='sm:col-start-3 lg:col-start-1 sm:col-span-8 lg:col-span-12'>
          <QuestionsContainer />
        </div>
        <div className='sm:col-start-3 sm:col-span-8'>
          <BestPractices />
        </div>
      </div>
    </>
  );
};

export default HowItWorks;
